import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';

const Footer = () => {
    const { toast } = useToast();
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;

        setSubmitting(true);
        try {
            const response = await fetch('/api.php?action=newsletter_subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await response.json();

            if (data.success) {
                toast({
                    title: "¡Suscripción Exitosa!",
                    description: "Ahora recibirás las mejores ofertas y noticias inmobiliarias.",
                    className: "bg-emerald-600 text-white font-bold border-none"
                });
                setEmail('');
            } else {
                toast({
                    title: "Error",
                    description: data.error || "No se pudo procesar la suscripción.",
                    variant: "destructive"
                });
            }
        } catch (error) {
            console.error("Newsletter error:", error);
            toast({
                title: "Error de conexión",
                description: "Por favor intenta de nuevo más tarde.",
                variant: "destructive"
            });
        }
        setSubmitting(false);
    };

    return (
        <footer className="bg-primary text-white pt-10 pb-24 sm:pb-12 relative z-10">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                    {/* Brand & Contact Info */}
                    <div className="space-y-4">
                        <div className="text-2xl font-bold tracking-tight text-white">
                            Servicios <span className="text-secondary">Futura</span>
                        </div>
                        <p className="text-white leading-relaxed text-sm">
                            Tu sistema diario para publicar propiedades, recibir leads directos y gestionar tu negocio inmobiliario en El Salvador.
                        </p>

                        <div className="space-y-3 pt-2">
                            <div className="flex items-start space-x-3 text-white">
                                <MapPin className="h-4 w-4 text-secondary mt-1 shrink-0" />
                                <span className="text-sm">Av. Independencia Sur, Santa Ana</span>
                            </div>
                            <a href="mailto:info@serviciosfutura.com" className="flex items-center space-x-3 text-white hover:text-secondary transition-colors">
                                <Mail className="h-4 w-4 text-secondary shrink-0" />
                                <span className="text-sm">info@serviciosfutura.com</span>
                            </a>
                            <a href="https://wa.me/50369826270" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-white hover:text-secondary transition-colors">
                                <Phone className="h-4 w-4 text-secondary shrink-0" />
                                <span className="text-sm">+503 6982-6270</span>
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-lg font-bold mb-4 text-secondary">Navegación</h3>
                        <ul className="space-y-2">
                            {[
                                { name: 'Inicio', path: '/' },
                                { name: 'Centro Inmobiliario', path: '/propiedades' },
                                { name: 'Herramientas', path: '/centro-financiero' },
                                { name: 'Planes', path: '/planes' },
                            ].map((link) => (
                                <li key={link.name}>
                                    <Link to={link.path} className="text-white hover:text-secondary transition-colors block py-0.5 text-sm">
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal & Support */}
                    <div>
                        <h3 className="text-lg font-bold mb-4 text-secondary">Soporte</h3>
                        <ul className="space-y-2">
                            {[
                                { name: 'Contacto', path: '/contacto' },
                                { name: 'Quiénes Somos', path: '/nosotros' },
                                { name: 'Términos y Condiciones', path: '/terminos' },
                                { name: 'Política de Privacidad', path: '/privacidad' },
                            ].map((link) => (
                                <li key={link.name}>
                                    <Link to={link.path} className="text-white hover:text-secondary transition-colors block py-0.5 text-sm">
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Social & Newsletter */}
                    <div>
                        <h3 className="text-lg font-bold mb-4 text-secondary">Síguenos</h3>
                        <div className="flex space-x-4 mb-6">
                            <a href="https://facebook.com/serviciosfuturasv" target="_blank" rel="noopener noreferrer" className="bg-white/20 p-2 rounded-full hover:bg-white hover:text-primary transition-all text-white">
                                <Facebook className="h-4 w-4" />
                            </a>
                            <a href="https://instagram.com/serviciosfuturasv" target="_blank" rel="noopener noreferrer" className="bg-white/20 p-2 rounded-full hover:bg-white hover:text-primary transition-all text-white">
                                <Instagram className="h-4 w-4" />
                            </a>
                            <a href="https://twitter.com/serviciosfutura" target="_blank" rel="noopener noreferrer" className="bg-white/20 p-2 rounded-full hover:bg-white hover:text-primary transition-all text-white">
                                <Twitter className="h-4 w-4" />
                            </a>
                        </div>
                        <div className="bg-white/10 p-4 rounded-xl border border-white/20 backdrop-blur-sm">
                            <p className="text-xs text-white mb-2 font-bold">Suscríbete a nuestro boletín</p>
                            <form onSubmit={handleSubscribe} className="flex gap-2">
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Tu email principal"
                                    className="bg-white/20 border-white/30 text-white placeholder:text-gray-300 h-9 text-xs focus-visible:ring-secondary"
                                    required
                                />
                                <Button
                                    type="submit"
                                    size="sm"
                                    disabled={submitting}
                                    className="bg-secondary hover:bg-secondary/90 text-white h-9 px-4 font-bold transition-all active:scale-95"
                                >
                                    {submitting ? '...' : 'Ir'}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="border-t border-white/20 pt-5 text-center text-white/80 text-[10px] uppercase font-bold tracking-widest">
                    <p>&copy; {new Date().getFullYear()} Servicios Futura · El Salvador. Todos los derechos reservados.</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
